/**
 * Batch Import Modal Component
 *
 * Supports importing tasks via Excel/CSV file upload.
 * Parses the file client-side, validates rows, then calls tasksApi.createBatch.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileSpreadsheet, FileText, Download, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { importTasks, downloadTaskTemplate, type ImportResult } from "@/lib/import-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi } from "@/api";

interface BatchImportModalProps {
  type: 'tasks' | 'keywords';
  trigger?: React.ReactNode;
}

export function BatchImportModal({ type, trigger }: BatchImportModalProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult<any> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Batch create tasks via API
  const createTasksMutation = useMutation({
    mutationFn: async (tasks: any[]) => {
      const urls = tasks.map((t: any) => t.target_url || t.url).filter(Boolean);
      if (urls.length === 0) throw new Error("No valid URLs found");
      const keyword = tasks[0]?.keyword || undefined;
      const project_id = tasks[0]?.project_id || undefined;
      return tasksApi.createBatch({ urls, keyword, project_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error("不支援的檔案格式，請使用 Excel (.xlsx, .xls) 或 CSV (.csv)");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("檔案大小不能超過 10MB");
      return;
    }

    setFile(selectedFile);
    setImportResult(null);
    setProgress(0);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("請選擇檔案");
      return;
    }

    setImporting(true);
    setProgress(0);
    setImportResult(null);

    try {
      const result = await importTasks(file, {
        onProgress: (p) => setProgress(p),
      });

      if (result.success.length > 0) {
        await createTasksMutation.mutateAsync(result.success);
      }

      setImportResult(result);

      if (result.success.length > 0) {
        toast.success(`成功匯入 ${result.success.length} 筆任務`);
      }

      if (result.failed.length > 0) {
        toast.warning(`${result.failed.length} 筆資料匯入失敗，請查看詳細資訊`);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFile(null);
    } catch (error: any) {
      toast.error(`匯入失敗: ${error.message}`);
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  const handleDownloadTemplate = () => {
    downloadTaskTemplate();
    toast.success("範本檔案已下載");
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setImportResult(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            批次匯入
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>批次匯入任務</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>選擇檔案</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={importing}
              />
              <label htmlFor="file-upload" className="flex-1 cursor-pointer">
                <div className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 hover:bg-muted transition-colors">
                  {file ? (
                    <>
                      {file.name.endsWith('.csv') ? (
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      ) : (
                        <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                      )}
                      <div className="text-sm">
                        <p className="font-medium">{file.name}</p>
                        <p className="text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div className="text-sm text-muted-foreground">
                        <p>點擊選擇檔案或拖放檔案到此處</p>
                        <p>支援 Excel (.xlsx, .xls) 或 CSV (.csv)</p>
                      </div>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">不確定格式？下載範本檔案參考</p>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              下載範本
            </Button>
          </div>

          {/* Progress */}
          {importing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>匯入中...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="space-y-2">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p>總共處理: {importResult.total} 筆</p>
                    <p className="text-green-600">成功: {importResult.success.length} 筆</p>
                    {importResult.failed.length > 0 && (
                      <p className="text-red-600">失敗: {importResult.failed.length} 筆</p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>

              {importResult.failed.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      <p className="font-medium">失敗的資料：</p>
                      {importResult.failed.map((item, index) => (
                        <p key={index} className="text-xs">
                          第 {item.row} 行: {item.error}
                        </p>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              關閉
            </Button>
            <Button onClick={handleImport} disabled={!file || importing}>
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  匯入中...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  開始匯入
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
